import React, { useState, useEffect } from 'react';
import { consentFormAPI } from '../../services/api';
import { format } from 'date-fns';

const DEFAULT_SECTIONS = [
  {
    key: 'section_services_text',
    title: '1. Nature of Counseling Services',
    text: `Therapy involves a collaborative relationship between therapist and client aimed at supporting emotional well-being and personal growth. Sessions typically last 50 minutes and may include talk therapy, psychoeducation, and evidence-based techniques. The therapist's role is to listen, guide, and support — not to give advice or make decisions on your behalf.`
  },
  {
    key: 'section_confidentiality_text',
    title: '2. Confidentiality',
    text: `All information shared in therapy is kept strictly confidential. Exceptions include situations where disclosure is legally required: (a) risk of imminent harm to yourself or others, (b) mandated reporting of child or elder abuse, or (c) a valid court order. In these limited circumstances, only information necessary to prevent harm or comply with legal obligations will be shared.`
  },
  {
    key: 'section_risks_text',
    title: '3. Risks & Benefits',
    text: `Therapy can be highly beneficial, but it may also involve temporary emotional discomfort as you explore difficult topics. Progress is not always linear. Benefits include improved mental health, coping strategies, and quality of life. You have the right to discuss concerns about the treatment approach at any time.`
  },
  {
    key: 'section_fees_text',
    title: '4. Session Fees & Cancellation Policy',
    text: `Session fees will be discussed and agreed upon before therapy begins. Payment is expected at the time of each session unless other arrangements are made. Please provide at least 24 hours' notice to cancel or reschedule a session. Late cancellations or no-shows may be charged the full session fee.`
  },
  {
    key: 'section_emergency_text',
    title: '5. Emergency Procedures',
    text: `This service is not a crisis line and is not designed for psychiatric emergencies. If you are in immediate danger or experiencing a mental health crisis, please contact emergency services (112/911), go to your nearest emergency room, or call a crisis helpline. Your therapist will discuss local emergency resources with you.`
  },
  {
    key: 'section_rights_text',
    title: '6. Client Rights',
    text: `You have the right to ask questions about your treatment at any time, to withdraw consent and discontinue therapy at any time without penalty, to request a referral to another therapist or service, and to access information about your records as permitted by law. Your participation in therapy is entirely voluntary.`
  }
];

const defaultSections = () =>
  DEFAULT_SECTIONS.map(s => ({ key: s.key, title: s.title, text: s.text }));

// Build sections for display from a sent/signed consent form
const buildSentSections = (form) => {
  if (form && Array.isArray(form.sections_json) && form.sections_json.length > 0) {
    return form.sections_json;
  }
  return DEFAULT_SECTIONS.map(s => ({
    key: s.key,
    title: s.title,
    text: (form && form[s.key]) ? form[s.key] : s.text
  }));
};

const ConsentFormTherapist = ({ userId, partnerId, userName }) => {
  const [consentForm, setConsentForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [error, setError] = useState('');

  // Editable sections array (therapist's template)
  const [sections, setSections] = useState(defaultSections);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateSaveSuccess, setTemplateSaveSuccess] = useState(false);

  // Add-section form
  const [addingSection, setAddingSection] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newSectionText, setNewSectionText] = useState('');

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [formRes, templateRes] = await Promise.allSettled([
        consentFormAPI.get(userId),
        consentFormAPI.getTemplate()
      ]);

      if (formRes.status === 'fulfilled') {
        setConsentForm(formRes.value.data.consentForm);
      }
      // Load saved template only if form not sent yet
      if (
        templateRes.status === 'fulfilled' &&
        Array.isArray(templateRes.value.data.template) &&
        templateRes.value.data.template.length > 0
      ) {
        setSections(templateRes.value.data.template);
      }
    } catch (err) {
      console.error('Failed to fetch consent form data:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Section editing ────────────────────────────────────────────────────────

  const handleSectionChange = (key, field, value) => {
    setSections(prev => prev.map(s => s.key === key ? { ...s, [field]: value } : s));
  };

  const handleDeleteSection = (key) => {
    setSections(prev => prev.filter(s => s.key !== key));
  };

  const handleResetSection = (key) => {
    const def = DEFAULT_SECTIONS.find(s => s.key === key);
    if (def) {
      setSections(prev => prev.map(s => s.key === key ? { ...s, title: def.title, text: def.text } : s));
    }
  };

  const handleResetAll = () => {
    setSections(defaultSections());
  };

  // ── Add section ────────────────────────────────────────────────────────────

  const handleAddSection = () => {
    if (!newSectionTitle.trim()) return;
    const newKey = `custom_${Date.now()}`;
    setSections(prev => [...prev, {
      key: newKey,
      title: newSectionTitle.trim(),
      text: newSectionText.trim()
    }]);
    setNewSectionTitle('');
    setNewSectionText('');
    setAddingSection(false);
  };

  // ── Save template ──────────────────────────────────────────────────────────

  const handleSaveTemplate = async () => {
    try {
      setSavingTemplate(true);
      setError('');
      await consentFormAPI.saveTemplate(sections);
      setTemplateSaveSuccess(true);
      setTimeout(() => setTemplateSaveSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save template. Please try again.');
    } finally {
      setSavingTemplate(false);
    }
  };

  // ── Send form ──────────────────────────────────────────────────────────────

  const handleSend = async () => {
    try {
      setSending(true);
      setError('');
      const response = await consentFormAPI.send(userId, { sections_json: sections });
      setConsentForm(response.data.consentForm);
      setPreviewMode(false);
    } catch (err) {
      if (err.response?.status === 409) {
        setConsentForm(err.response.data.consentForm);
      } else {
        setError(err.response?.data?.error || 'Failed to send consent form. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  // ── PDF ────────────────────────────────────────────────────────────────────

  const handleDownloadPDF = () => {
    if (!consentForm) return;

    const safeVal = (v) => v || '—';
    const safeDate = (d) => {
      if (!d) return '—';
      try { return format(new Date(d), 'MMMM d, yyyy'); } catch { return d; }
    };

    const sectionsForPDF = buildSentSections(consentForm);

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Informed Consent — ${safeVal(consentForm.client_full_name)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Georgia, serif; font-size: 12pt; color: #111; padding: 40px; line-height: 1.6; }
    h1 { font-size: 18pt; text-align: center; margin-bottom: 4px; }
    .subtitle { text-align: center; font-size: 10pt; color: #555; margin-bottom: 24px; }
    hr { border: none; border-top: 2px solid #333; margin: 20px 0; }
    .section { margin-bottom: 20px; }
    .section h2 { font-size: 11pt; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #ccc; padding-bottom: 4px; margin-bottom: 10px; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; }
    .field { margin-bottom: 8px; }
    .field .label { font-size: 9pt; color: #666; text-transform: uppercase; letter-spacing: 0.3px; }
    .field .value { font-size: 11pt; border-bottom: 1px solid #ddd; padding-bottom: 2px; min-height: 20px; }
    .consent-section { margin-bottom: 14px; }
    .consent-section h3 { font-size: 11pt; font-weight: bold; margin-bottom: 4px; }
    .consent-section p { font-size: 10pt; color: #333; }
    .ack-check { display: inline-block; width: 12px; height: 12px; border: 1px solid #333; text-align: center; line-height: 12px; font-size: 9pt; margin-right: 6px; }
    .signature-box { border: 1px solid #aaa; padding: 10px 16px; font-size: 14pt; font-style: italic; min-height: 40px; margin-top: 4px; }
    .footer { margin-top: 30px; font-size: 9pt; color: #888; text-align: center; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <h1>Informed Consent for Counseling Services</h1>
  <p class="subtitle">TheraP Track &nbsp;|&nbsp; Generated on ${format(new Date(), 'MMMM d, yyyy')}</p>
  <hr />

  <div class="section">
    <h2>Personal Information</h2>
    <div class="grid">
      <div class="field"><div class="label">Full Name</div><div class="value">${safeVal(consentForm.client_full_name)}</div></div>
      <div class="field"><div class="label">Date of Birth</div><div class="value">${safeDate(consentForm.client_date_of_birth)}</div></div>
      <div class="field"><div class="label">Phone</div><div class="value">${safeVal(consentForm.client_phone)}</div></div>
      <div class="field"><div class="label">Email</div><div class="value">${safeVal(consentForm.client_email)}</div></div>
      <div class="field" style="grid-column: span 2"><div class="label">Address</div><div class="value">${safeVal(consentForm.client_address)}</div></div>
    </div>
  </div>

  <div class="section">
    <h2>Emergency Contact</h2>
    <div class="grid">
      <div class="field"><div class="label">Name</div><div class="value">${safeVal(consentForm.emergency_contact_name)}</div></div>
      <div class="field"><div class="label">Phone</div><div class="value">${safeVal(consentForm.emergency_contact_phone)}</div></div>
    </div>
  </div>

  ${consentForm.therapy_goals ? `
  <div class="section">
    <h2>Therapy Goals</h2>
    <div class="field"><div class="value" style="border: 1px solid #ddd; padding: 8px; white-space: pre-wrap;">${consentForm.therapy_goals}</div></div>
  </div>` : ''}

  <div class="section">
    <h2>Terms &amp; Conditions</h2>
    ${sectionsForPDF.map(s => `
    <div class="consent-section">
      <h3>${s.title}</h3>
      <p>${s.text}</p>
    </div>`).join('')}
  </div>

  <div class="section">
    <h2>Acknowledgment</h2>
    <p style="font-size:11pt;">
      <span class="ack-check">&#10003;</span>
      The client has read and agreed to all terms and conditions stated above.
    </p>
  </div>

  ${consentForm.additional_notes ? `
  <div class="section">
    <h2>Additional Notes</h2>
    <div class="field"><div class="value" style="border: 1px solid #ddd; padding: 8px; white-space: pre-wrap;">${consentForm.additional_notes}</div></div>
  </div>` : ''}

  <hr />

  <div class="section">
    <h2>Digital Signature</h2>
    <div class="grid">
      <div class="field">
        <div class="label">Client Signature</div>
        <div class="signature-box">${safeVal(consentForm.client_signature)}</div>
      </div>
      <div class="field">
        <div class="label">Date Signed</div>
        <div class="value" style="margin-top: 16px; font-size: 12pt;">${safeDate(consentForm.signature_date || consentForm.signed_at)}</div>
      </div>
    </div>
  </div>

  <div class="footer">
    This document was generated by TheraP Track. Form sent on ${safeDate(consentForm.sent_at)} and signed on ${safeDate(consentForm.signed_at)}.
  </div>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (!win) {
      alert('Please allow pop-ups to download the PDF.');
      return;
    }
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  const getStatusBadge = () => {
    if (!consentForm) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
          Not Sent
        </span>
      );
    }
    if (consentForm.status === 'pending') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
          Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
        Signed
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Sections to display in the sent/signed preview (read-only)
  const sentSections = consentForm ? buildSentSections(consentForm) : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Consent Form</h3>
        {getStatusBadge()}
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* ── NOT SENT ────────────────────────────────────────────── */}
      {!consentForm && (
        <div className="card space-y-4">
          <p className="text-gray-600 dark:text-dark-text-secondary">
            Send an informed consent form to <strong>{userName}</strong> before beginning therapy.
            You can customise the sections below — add new ones, remove any you don't need, or edit the text.
            Your template is saved per your account and reused for all clients.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleSend}
              disabled={sending || sections.length === 0}
              className="btn btn-primary px-6 py-2 disabled:opacity-50"
            >
              {sending ? 'Sending...' : `Send Consent Form to ${userName}`}
            </button>
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className="btn btn-secondary px-4 py-2 text-sm"
            >
              {previewMode ? 'Hide Editor' : 'Customise & Preview Form'}
            </button>
          </div>
        </div>
      )}

      {/* ── PENDING ─────────────────────────────────────────────── */}
      {consentForm?.status === 'pending' && (
        <div className="space-y-3">
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-yellow-900 dark:text-yellow-200">Awaiting client signature</p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  Sent on {consentForm.sent_at ? format(new Date(consentForm.sent_at), 'MMM d, yyyy') : '—'}.
                  The client will sign it through their dashboard.
                </p>
              </div>
              <button
                onClick={() => setPreviewMode(!previewMode)}
                className="btn btn-secondary px-4 py-2 text-sm ml-4 flex-shrink-0"
              >
                {previewMode ? 'Hide Preview' : 'Preview Sent Form'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SIGNED ──────────────────────────────────────────────── */}
      {consentForm?.status === 'signed' && (
        <div className="space-y-4">
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-green-900 dark:text-green-200">Consent form signed</p>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  Signed on {consentForm.signed_at ? format(new Date(consentForm.signed_at), 'MMM d, yyyy') : '—'}
                </p>
              </div>
              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                <button
                  onClick={() => setViewMode(!viewMode)}
                  className="btn btn-secondary px-4 py-2 text-sm"
                >
                  {viewMode ? 'Hide Form' : 'View Signed Form'}
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className="btn btn-secondary px-4 py-2 text-sm flex items-center gap-1.5"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  </svg>
                  Download PDF
                </button>
              </div>
            </div>
          </div>

          {/* Signed form details */}
          {viewMode && (
            <div className="card space-y-6">
              <h4 className="font-semibold text-gray-900 dark:text-dark-text-primary border-b border-gray-200 dark:border-dark-border pb-3">
                Signed Consent Form — {userName}
              </h4>

              <div>
                <h5 className="text-sm font-semibold text-gray-700 dark:text-dark-text-secondary uppercase tracking-wide mb-3">Personal Details</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ReadField label="Full Name" value={consentForm.client_full_name} />
                  <ReadField label="Date of Birth" value={consentForm.client_date_of_birth ? format(new Date(consentForm.client_date_of_birth), 'MMM d, yyyy') : null} />
                  <ReadField label="Phone" value={consentForm.client_phone} />
                  <ReadField label="Email" value={consentForm.client_email} />
                  <div className="md:col-span-2">
                    <ReadField label="Address" value={consentForm.client_address} />
                  </div>
                </div>
              </div>

              <div>
                <h5 className="text-sm font-semibold text-gray-700 dark:text-dark-text-secondary uppercase tracking-wide mb-3">Emergency Contact</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ReadField label="Name" value={consentForm.emergency_contact_name} />
                  <ReadField label="Phone" value={consentForm.emergency_contact_phone} />
                </div>
              </div>

              {consentForm.therapy_goals && (
                <div>
                  <h5 className="text-sm font-semibold text-gray-700 dark:text-dark-text-secondary uppercase tracking-wide mb-3">Therapy Goals</h5>
                  <p className="text-sm text-gray-700 dark:text-dark-text-secondary bg-gray-50 dark:bg-dark-card-secondary p-3 rounded-lg">
                    {consentForm.therapy_goals}
                  </p>
                </div>
              )}

              <div>
                <h5 className="text-sm font-semibold text-gray-700 dark:text-dark-text-secondary uppercase tracking-wide mb-3">Acknowledgment</h5>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-green-500 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm text-gray-700 dark:text-dark-text-secondary">
                    Client has read and agreed to all terms and conditions.
                  </span>
                </div>
              </div>

              {consentForm.additional_notes && (
                <div>
                  <h5 className="text-sm font-semibold text-gray-700 dark:text-dark-text-secondary uppercase tracking-wide mb-3">Additional Notes</h5>
                  <p className="text-sm text-gray-700 dark:text-dark-text-secondary bg-gray-50 dark:bg-dark-card-secondary p-3 rounded-lg">
                    {consentForm.additional_notes}
                  </p>
                </div>
              )}

              <div className="border-t border-gray-200 dark:border-dark-border pt-4">
                <h5 className="text-sm font-semibold text-gray-700 dark:text-dark-text-secondary uppercase tracking-wide mb-3">Digital Signature</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ReadField label="Signature" value={consentForm.client_signature} />
                  <ReadField label="Signature Date" value={consentForm.signature_date ? format(new Date(consentForm.signature_date), 'MMM d, yyyy') : null} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── PREVIEW / EDIT PANEL ────────────────────────────────── */}
      {previewMode && (
        <div className="card border border-gray-200 dark:border-dark-border space-y-5">
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-dark-border pb-3">
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-dark-text-primary">
                {!consentForm ? 'Customise Form Content' : 'Form Content (Sent)'}
              </h4>
              {!consentForm && (
                <p className="text-xs text-gray-500 dark:text-dark-text-tertiary mt-0.5">
                  Edit section titles and text, remove sections with ×, or add new ones below.
                </p>
              )}
            </div>
            {!consentForm && (
              <button
                onClick={handleResetAll}
                className="text-xs text-primary-600 hover:text-primary-700 dark:text-dark-primary-400 font-medium underline underline-offset-2 flex-shrink-0 ml-4"
              >
                Reset all to default
              </button>
            )}
          </div>

          {/* ── Editable sections ── */}
          {!consentForm && (
            <div className="space-y-4">
              {sections.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-dark-text-tertiary italic text-center py-4">
                  No sections. Add one below.
                </p>
              )}
              {sections.map((section) => {
                const isDefault = DEFAULT_SECTIONS.some(d => d.key === section.key);
                const def = DEFAULT_SECTIONS.find(d => d.key === section.key);
                const isModified = isDefault && (section.title !== def.title || section.text !== def.text);

                return (
                  <div key={section.key} className="border border-gray-200 dark:border-dark-border rounded-lg p-4 space-y-3 bg-gray-50 dark:bg-dark-card-secondary">
                    {/* Section header row */}
                    <div className="flex items-start gap-2">
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) => handleSectionChange(section.key, 'title', e.target.value)}
                        className="input flex-1 text-sm font-semibold py-1"
                        placeholder="Section title"
                      />
                      <div className="flex items-center gap-1 flex-shrink-0 mt-1">
                        {isModified && (
                          <button
                            type="button"
                            onClick={() => handleResetSection(section.key)}
                            title="Reset to default"
                            className="text-xs text-gray-500 hover:text-gray-700 dark:text-dark-text-tertiary dark:hover:text-dark-text-secondary underline underline-offset-2 px-1"
                          >
                            Reset
                          </button>
                        )}
                        {isModified && (
                          <span className="text-xs text-primary-600 dark:text-dark-primary-400 font-medium mr-1">(edited)</span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDeleteSection(section.key)}
                          title="Delete section"
                          className="w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <textarea
                      value={section.text}
                      onChange={(e) => handleSectionChange(section.key, 'text', e.target.value)}
                      rows={4}
                      className="input w-full text-sm leading-relaxed resize-y"
                      placeholder="Section content..."
                    />
                  </div>
                );
              })}

              {/* ── Add section ── */}
              {addingSection ? (
                <div className="border-2 border-dashed border-primary-300 dark:border-dark-primary-600 rounded-lg p-4 space-y-3 bg-primary-50/30 dark:bg-dark-primary-900/10">
                  <p className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary">New Section</p>
                  <input
                    type="text"
                    value={newSectionTitle}
                    onChange={(e) => setNewSectionTitle(e.target.value)}
                    className="input w-full text-sm"
                    placeholder="Section title (e.g. 7. Telehealth Policy)"
                    autoFocus
                  />
                  <textarea
                    value={newSectionText}
                    onChange={(e) => setNewSectionText(e.target.value)}
                    rows={3}
                    className="input w-full text-sm leading-relaxed resize-y"
                    placeholder="Section content..."
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleAddSection}
                      disabled={!newSectionTitle.trim()}
                      className="btn btn-primary px-4 py-1.5 text-sm disabled:opacity-50"
                    >
                      Add Section
                    </button>
                    <button
                      type="button"
                      onClick={() => { setAddingSection(false); setNewSectionTitle(''); setNewSectionText(''); }}
                      className="btn btn-secondary px-4 py-1.5 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingSection(true)}
                  className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 dark:border-dark-border rounded-lg text-sm text-gray-500 dark:text-dark-text-tertiary hover:border-primary-400 hover:text-primary-600 dark:hover:border-dark-primary-500 dark:hover:text-dark-primary-400 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  Add New Section
                </button>
              )}

              {/* ── Save template ── */}
              <div className="pt-2 border-t border-gray-200 dark:border-dark-border flex items-center justify-between">
                <p className="text-xs text-gray-500 dark:text-dark-text-tertiary">
                  Save your template to reuse it for all clients.
                </p>
                <div className="flex items-center gap-3">
                  {templateSaveSuccess && (
                    <span className="text-xs text-green-600 dark:text-green-400 font-medium flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      Saved
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={handleSaveTemplate}
                    disabled={savingTemplate}
                    className="btn btn-secondary px-4 py-1.5 text-sm disabled:opacity-50"
                  >
                    {savingTemplate ? 'Saving...' : 'Save Template'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Read-only view (form already sent) ── */}
          {consentForm && (
            <div className="space-y-5">
              {sentSections.map((section, i) => (
                <div key={i} className="space-y-2">
                  <h5 className="text-sm font-semibold text-gray-800 dark:text-dark-text-primary">
                    {section.title}
                  </h5>
                  <p className="text-sm text-gray-600 dark:text-dark-text-secondary leading-relaxed bg-gray-50 dark:bg-dark-card-secondary rounded-lg p-3">
                    {section.text}
                  </p>
                </div>
              ))}
              <div className="pt-2 border-t border-gray-200 dark:border-dark-border">
                <p className="text-xs text-gray-500 dark:text-dark-text-tertiary">
                  The client was also asked to provide their personal details, emergency contact, therapy goals, and a digital signature.
                </p>
              </div>
            </div>
          )}

          {!consentForm && (
            <div className="pt-1">
              <p className="text-xs text-gray-500 dark:text-dark-text-tertiary">
                The client will also be asked to provide their personal details, emergency contact, therapy goals, and a digital signature.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ReadField = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500 dark:text-dark-text-tertiary mb-1">{label}</p>
    <p className="text-sm text-gray-800 dark:text-dark-text-primary">
      {value || <span className="text-gray-400 dark:text-dark-text-tertiary italic">—</span>}
    </p>
  </div>
);

export default ConsentFormTherapist;
